import {
  ConditionGroup,
  ReachSegment,
  ParamSchema,
  SegmentPageKind,
  SegmentCondition,
  SegmentType,
} from "../../../apis/requests/segments/types";
import {
  DEFAULT_SEGMENT_TYPE,
  FEATURE_FLAG_PARAMETER,
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
  VALIDATION_SEGMENT_TYPE,
  EVERYONE_ELSE_LABEL,
} from "./constants";
import type {FormValues} from "./schema";

export const splitFlagValue = (v: string): {name: string; variant: string} => {
  const idx = v.indexOf(":");
  return idx === -1 ? {name: v, variant: ""} : {name: v.slice(0, idx), variant: v.slice(idx + 1)};
};

export const joinFlagValue = (name: string, variant: string): string => `${name}:${variant}`;

export const findParam = (schema: ParamSchema[] | undefined, parameter: string): ParamSchema | undefined =>
  schema?.find((p) => p.parameter === parameter);

export const defaultValueForCondition = (param: ParamSchema | undefined, operator: string): unknown => {
  if (!param) return null;
  if (NO_VALUE_OPERATORS.has(operator)) return null;
  if (MULTI_VALUE_OPERATORS.has(operator)) return [];
  if (param.kind === "numeric") return "";
  if (param.parameter === FEATURE_FLAG_PARAMETER) {
    const first = param.options?.[0];
    return first ? joinFlagValue(first.group ?? "", first.value) : "";
  }
  return param.default_value ?? "";
};

export const makeDefaultCondition = (parameters: ParamSchema[]) => {
  const first = parameters[0];
  const operator = first?.operators[0] ?? "";
  return {
    parameter: first?.parameter ?? "",
    operator,
    value: defaultValueForCondition(first, operator),
  };
};

export const normalizeSubmitValue = (
  param: ParamSchema | undefined,
  operator: string,
  rawValue: unknown
): SegmentCondition["value"] => {
  if (!param) return null;
  if (NO_VALUE_OPERATORS.has(operator)) return null;
  if (MULTI_VALUE_OPERATORS.has(operator)) {
    if (Array.isArray(rawValue)) return rawValue.map((v) => String(v));
    if (typeof rawValue === "string" && rawValue.length > 0) {
      return rawValue
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }
  if (param.kind === "numeric") {
    const n = typeof rawValue === "number" ? rawValue : parseFloat(String(rawValue ?? ""));
    return Number.isFinite(n) ? n : null;
  }
  return rawValue === null || rawValue === undefined ? "" : String(rawValue);
};

export const buildConditionsPayload = (values: FormValues, parameters: ParamSchema[]): ConditionGroup => {
  const segmentName = values.name.trim();
  const isSingleGroup = values.groups.length === 1;
  return {
    match_mode: values.group_match_mode,
    rules: values.groups.map((g, i) => ({
      name: g.name || (isSingleGroup ? segmentName : `Group ${i + 1}`),
      match_mode: g.match_mode,
      rules: g.conditions.map((c) => {
        const param = findParam(parameters, c.parameter);
        return {
          parameter: c.parameter,
          operator: c.operator,
          value: normalizeSubmitValue(param, c.operator, c.value),
        };
      }),
    })),
  };
};

export const firstErrorMessage = (node: unknown): string | null => {
  if (!node || typeof node !== "object") return null;
  if ("message" in node && typeof node.message === "string" && node.message) return node.message;
  for (const child of Object.values(node)) {
    const found = firstErrorMessage(child);
    if (found) return found;
  }
  return null;
};

export const isValidationSegment = (segment: {type: SegmentType}) => segment.type === VALIDATION_SEGMENT_TYPE;

export const segmentPageKindOf = (segment: {type: SegmentType}): SegmentPageKind =>
  isValidationSegment(segment) ? "validation" : "marketing";

export const segmentCategoryFromForm = ({type, segment_category}: Pick<FormValues, "type" | "segment_category">) =>
  type === DEFAULT_SEGMENT_TYPE ? segment_category || null : null;

export const categoryBucketLabel = (segment: Pick<ReachSegment, "name" | "is_category_fallback">) =>
  segment.is_category_fallback ? EVERYONE_ELSE_LABEL : segment.name;
